import { required, email, numeric, minLength, maxLength, sameAs, minValue, maxValue } from 'vuelidate/lib/validators';
import { VuelidateValidationClass } from './types';
import * as Validators from './validators';
import * as Messages from './messages';
import { exactLength, validRoutingNumber } from './validators/index';
import { resolvePath } from '../utils/object';
import { camelCase, upperCaseFirst } from 'change-case';
// Re-use the name/path extrapolation for $v properties.
const getValidationObject = ($this, name) => {
    const vuelidateObject = $this.$v;
    let validationObject;
    if (name.indexOf('.') > -1) {
        validationObject = resolvePath(name, vuelidateObject);
    }
    else {
        validationObject = vuelidateObject[name];
    }
    return validationObject;
};
export const emailValidation = {
    validations: {
        required,
        email
    },
    errors($this, name = 'email') {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (!validationObject.required) {
            errors.push('Field is required.');
        }
        if (!validationObject.email) {
            errors.push('Please enter a valid email address.');
        }
        return errors;
    }
};
export const requiredOnlyValidation = {
    validations: {
        required
    },
    errors($this, name, label = 'Field') {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (!validationObject.required) {
            errors.push(`${label} is required.`);
        }
        return errors;
    }
};
export class ConfirmValidation extends VuelidateValidationClass {
    static validations(confirmationProperty) {
        let confirmationPropertyLabel = upperCaseFirst(camelCase(confirmationProperty));
        let sameAsPropertyName = `sameAs${confirmationPropertyLabel}`;
        // Original functionality.
        return {
            required,
            // By using the sameAs function option, we allow the confirmationProperty to be relative the property
            // being sameAs'd so you would register the error handler like this:
            //    For flat properties:
            //      ConfirmValidation.errors(this, 'passwordConfirm', 'password');
            //    For nested properties:
            //      ConfirmValidation.errors(this, 'data.sftpConnection.passwordConfirm', 'password');
            [sameAsPropertyName]: sameAs(function (model) {
                return resolvePath(confirmationProperty, model);
            }),
        };
    }
    static errors($this, name, other, label = 'Field') {
        const errors = [];
        const upperOther = upperCaseFirst(camelCase(other));
        const sameAsPropertyName = `sameAs${upperOther}`;
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (!validationObject.required) {
            errors.push(Messages.required.replace('{1}', label));
        }
        if (!validationObject[sameAsPropertyName]) {
            errors.push(Messages.sameAs.replace('{1}', upperOther));
        }
        return errors;
    }
}
export const newPasswordRules = {
    validations: {
        required,
        minLength: minLength(8),
        hasUppercase: Validators.hasUppercase,
        hasLowercase: Validators.hasLowercase,
        hasNumber: Validators.hasNumber,
        hasSpecialCharacter: Validators.hasSpecialCharacter
    },
    errors($this, label = 'Password', minLength = 8, name = 'password') {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (!validationObject.required) {
            errors.push(Messages.required.replace('{1}', label));
        }
        if (!validationObject.minLength) {
            errors.push(Messages.minLength.replace('{1}', label).replace('{2}', minLength.toString()));
        }
        if (!validationObject.hasUppercase) {
            errors.push(Messages.hasUpper);
        }
        if (!validationObject.hasLowercase) {
            errors.push(Messages.hasLower);
        }
        if (!validationObject.hasNumber) {
            errors.push(Messages.hasNumber);
        }
        if (!validationObject.hasSpecialCharacter) {
            errors.push(Messages.hasSpecialCharacter);
        }
        return errors;
    }
};
export const confirmPasswordRules = {
    validations: {
        required,
        sameAsPassword: sameAs('password')
    },
    errors($this, name, other) {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        const label = upperCaseFirst(other);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (!validationObject.sameAsPassword) {
            errors.push(Messages.sameAs.replace('{1}', label));
        }
        return errors;
    }
};
export class CodeValidation extends VuelidateValidationClass {
    static validations(length = 6) {
        return {
            required,
            minLength: minLength(length),
            maxLength: maxLength(length),
            numeric
        };
    }
    static errors($this, name, length = 6, label = 'Field') {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (!validationObject.required) {
            errors.push(Messages.required.replace('{1}', label));
        }
        if (!validationObject.maxLength || !$this.$v[name].minLength) {
            errors.push(Messages.exactLength.replace('{1}', length.toString()));
        }
        if (!validationObject.numeric) {
            errors.push(Messages.numeric);
        }
        return errors;
    }
}
export class DateValidation extends VuelidateValidationClass {
    static validations(format) {
        return {
            required,
            dateFormat: Validators.dateFormat(format)
        };
    }
    static errors($this, name, format, label = 'Field') {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (!validationObject.required) {
            errors.push(Messages.required.replace('{1}', label));
        }
        if (!validationObject.dateFormat) {
            errors.push(Messages.dateFormat.replace('{1}', format));
        }
        return errors;
    }
}
export class NewUsernameValidation extends VuelidateValidationClass {
    static validations(callBack) {
        return {
            required,
            minLength: minLength(3),
            validUsername: Validators.validUsername,
            remote: Validators.remote(callBack)
        };
    }
    static errors($this, name = 'username', label = 'Username') {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (!validationObject.required) {
            errors.push(Messages.required.replace('{1}', label));
        }
        if (!validationObject.minLength) {
            errors.push(Messages.minLength.replace('{1}', label).replace('{2}', '3'));
        }
        if (!validationObject.validUsername) {
            errors.push(Messages.validUsername);
        }
        if (!validationObject.remote) {
            errors.push(Messages.usernameAvailable);
        }
        return errors;
    }
}
export class TenDigitPhoneValidation extends VuelidateValidationClass {
    static validations(isRequired = true) {
        const validation = {
            required,
            tenDigitPhone: Validators.tenDigitPhone
        };
        if (!isRequired) {
            delete validation.required;
        }
        return validation;
    }
    static errors($this, name = 'phone', label = 'Field', required = true) {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (required && !validationObject.required) {
            errors.push(Messages.required.replace('{1}', label));
        }
        if (!validationObject.tenDigitPhone) {
            errors.push(Messages.tenDigitPhone);
        }
        return errors;
    }
}
export class PinValidation extends VuelidateValidationClass {
    static validations(length = 4) {
        return {
            required,
            exactLength: Validators.exactLength(length),
            numeric,
        };
    }
    static errors($this, name, length = 4) {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (!validationObject.numeric) {
            errors.push(Messages.numeric);
        }
        if (!validationObject.exactLength) {
            errors.push(Messages.exactLength.replace('{1}', length.toString()));
        }
        if (!validationObject.required) {
            errors.push(Messages.required.replace('{1}', name));
        }
        return errors;
    }
}
export class AccountNumberValidation extends VuelidateValidationClass {
    static validations() {
        return {
            required,
            numeric,
        };
    }
    static errors($this, name, label = 'Field') {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (!validationObject.numeric) {
            errors.push(Messages.numeric);
        }
        if (!validationObject.required) {
            errors.push(Messages.required.replace('{1}', label));
        }
        return errors;
    }
}
export class RoutingNumberValidation extends VuelidateValidationClass {
    static validations(callback) {
        return {
            required,
            numeric,
            exactLength: exactLength(9),
            validRoutingNumber,
            remote: Validators.remote(callback),
        };
    }
    static errors($this, name, label = 'Field', fi = null) {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (!validationObject.numeric) {
            errors.push(Messages.numeric);
        }
        if (!validationObject.exactLength) {
            errors.push(Messages.exactLength.replace('{1}', '9'));
        }
        if (!validationObject.required) {
            errors.push(Messages.required.replace('{1}', label));
        }
        if (!validationObject.validRoutingNumber || (!validationObject.$pending && !validationObject.remote)) {
            if (fi !== null) {
                errors.push(Messages.routingNumberDoesNotMatchFi.replace('{1}', fi.name));
            }
            errors.push(Messages.routingNumber);
        }
        return errors;
    }
}
export class RequiredValidation extends VuelidateValidationClass {
    static validations() {
        return {
            required,
        };
    }
    static errors($this, name, label = 'Field') {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (!validationObject.required) {
            errors.push(Messages.required.replace('{1}', label));
        }
        return errors;
    }
}
export class CurrencyValidation extends VuelidateValidationClass {
    static validations(options = {
        isRequired: true,
    }) {
        if (!options.min) {
            options.min = 0;
        }
        if (!options.max) {
            options.max = 0;
        }
        const validation = {
            required,
            validCurrency: Validators.validCurrency,
            minValue: minValue(options.min),
            maxValue: maxValue(options.max)
        };
        if (!options.isRequired) {
            delete validation.required;
        }
        if (!options.hasMin) {
            delete validation.minValue;
        }
        if (!options.hasMax) {
            delete validation.maxValue;
        }
        return validation;
    }
    static errors($this, name, label = 'Field', options = {
        isRequired: true
    }) {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (required && !validationObject.required) {
            errors.push(Messages.required.replace('{1}', label));
        }
        if (!validationObject.validCurrency) {
            errors.push(Messages.currency);
        }
        if (options.hasMin && !validationObject.minValue) {
            errors.push(Messages.minValue.replace('{1}', options.min.toString()));
        }
        if (options.hasMax && !validationObject.maxValue) {
            errors.push(Messages.maxValue.replace('{1}', options.max.toString()));
        }
        return errors;
    }
}
export class LengthValidation extends VuelidateValidationClass {
    static validations(isRequired, min, max) {
        let minParam = (min) ? min : 0;
        let maxParam = (max) ? max : 0;
        let validation = {
            required,
            minLength: minLength(minParam),
            maxLength: maxLength(maxParam)
        };
        if (!isRequired) {
            delete validation.required;
        }
        if (!min) {
            delete validation.minLength;
        }
        if (!max) {
            delete validation.maxLength;
        }
        return validation;
    }
    static errors($this, name, min, max, label = 'Field', required = true) {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (required && !validationObject.required) {
            errors.push(Messages.required.replace('{1}', label));
        }
        if (!max) {
            if (!validationObject.minLength) {
                errors.push(Messages.minLength.replace('{1}', label).replace('{2}', min.toString()));
            }
        }
        else if (!min) {
            if (!validationObject.maxLength) {
                errors.push(Messages.maxLength.replace('{2}', max.toString()));
            }
        }
        else if (min === max) {
            if (!validationObject.maxLength || !validationObject.minLength) {
                errors.push(Messages.exactLength.replace('{1}', max.toString()));
            }
        }
        else {
            if (!validationObject.minLength) {
                errors.push(Messages.minLength.replace('{1}', label).replace('{2}', min.toString()));
            }
            if (!validationObject.maxLength) {
                errors.push(Messages.maxLength.replace('{2}', max.toString()));
            }
        }
        return errors;
    }
}
export class FtpHostValidation extends VuelidateValidationClass {
    static validations(isRequired = true) {
        const validation = {
            required,
            validFtpHost: Validators.validFtpHost
        };
        if (!isRequired) {
            delete validation.required;
        }
        return validation;
    }
    static errors($this, name = 'ftpHost', label = 'Field', required = true) {
        const errors = [];
        let validationObject = getValidationObject($this, name);
        if (!validationObject.$dirty) {
            return errors;
        }
        if (required && !validationObject.required) {
            errors.push(Messages.required.replace('{1}', label));
        }
        if (!validationObject.validFtpHost) {
            errors.push(Messages.invalidFtpHost);
        }
        return errors;
    }
}
